import type { WritableDraft } from 'immer';
import type { Security } from '../../../../types';
import type { SwapState } from './types';

/**
 * A crypto-crypto swap is one whose quote currency is not an org settlement currency.
 * For GCash that is any pair quoted in something other than USDT or PHP (e.g. BTC-ETH).
 * These must be executed against the RFQ rather than as a plain limit order.
 *
 * Every settlement currency must be resolved before we classify: org config currencies
 * (e.g. `favoredPairCurrency`) load asynchronously, so if any entry is still missing we
 * fall back to the limit order rather than risk routing a settlement-quoted pair as RFQ.
 */
export function isCryptoCryptoSwapSecurity(
  security: Security,
  settlementCurrencies: Array<string | undefined>
): boolean {
  if (settlementCurrencies.length === 0 || settlementCurrencies.some(symbol => !symbol)) {
    return false;
  }
  return !settlementCurrencies.includes(security.QuoteCurrency);
}

export const updateAvailableItemsForCurrencies = (state: WritableDraft<SwapState>) => {
  const availableCurrencies = getAvailableItemsForCurrencies(
    state.referenceData.currencies.currenciesList,
    state.referenceData.securities.securitiesList
  );
  state.form.fromCurrency = state.form.fromCurrency.updateAvailableItems(availableCurrencies);
  state.form.toCurrency = state.form.toCurrency.updateAvailableItems(availableCurrencies);
};

export const getAvailableItemsForCurrencies = (
  currenciesList: SwapState['referenceData']['currencies']['currenciesList'],
  securitiesList: SwapState['referenceData']['securities']['securitiesList']
) => {
  const currenciesSet = new Set(securitiesList.flatMap(security => [security.BaseCurrency, security.QuoteCurrency]));
  const availableCurrencies = currenciesList.filter(currency => currenciesSet.has(currency.Symbol));
  return availableCurrencies;
};
